'use client';
import { useEffect, useState } from 'react';
import Link from 'next/link';

import HeroCarousel from './components/HeroCarousel';
import StatsCard from './components/StatsCard';
import ServiceCard from './components/ServiceCard';

import ContactForm from './components/ContactForm';
import SafetyStatsCard from './components/SafetyStatsCard';
import SafetyCertificationsCard from './components/SafetyCertificationsCard';
import Footer from '@/components/common/Footer';
import Icon from '@/components/ui/AppIcon';
import Navigation from '@/components/common/Navigation';

const qaItems = [
  {
    q: "What exactly is horizontal directional drilling (HDD)?",
    a: "HDD is a trenchless method of installing underground pipes, conduits, and cables along a curved bore path. A surface-launched drill rig steers a drill head underground, then pulls the product pipe back through the bore — all without open-cut excavation.",
  },
  {
    q: "Will drilling damage my road, landscaping, or existing utilities?",
    a: "That's the core advantage of HDD. Because we drill beneath the surface, roads, sidewalks, driveways, and landscaping stay intact. We also conduct thorough utility locates before every bore to protect existing infrastructure.",
  },
  {
    q: "How deep and how far can you drill?",
    a: "We drill to depths of 100 feet and bore lengths up to 1,300 feet per shot. For longer runs, we stage multiple bores. Diameter capability ranges from 2\" to 24\", covering everything from fiber conduit to large-diameter gas mains.",
  },
  {
    q: "What ground conditions can you work in?",
    a: "Our fleet is equipped for dirt, soft rock, and solid rock. We deploy specialized tooling — air hammers, rock reamers, mud motors, and railhead assemblies — matched to the specific geology of each project site.",
  },
  {
    q: "How long does an underground drilling project take?",
    a: "Timeline depends on bore length, diameter, soil conditions, and site access. A standard utility crossing can be completed in one to three days. Larger infrastructure projects are scoped with a detailed schedule provided in your quote within 72 hours of inquiry.",
  },
  {
    q: "What industries and project types do you serve?",
    a: "We work across energy (natural gas, oil, electrical transmission), telecommunications (fiber, broadband, 5G), municipal utilities (water, sewer, stormwater), and industrial projects (solar, wind, data center infrastructure) throughout the East Coast.",
  },
];

export default function Homepage() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  // Scroll reveal animation
  useEffect(() => {
    const reveals = document.querySelectorAll('.reveal')
    if (!reveals?.length) return
    
    const observer = new IntersectionObserver(
      (entries) => entries.forEach(e => e.isIntersecting && e.target.classList.add('active')),
      { threshold: 0.1 }
    )
    reveals?.forEach(el => observer?.observe(el))
    
    return () => observer?.disconnect();
  }, [])

  return (
    <main className="min-h-screen bg-background">
      <Navigation />
      {/* Hero Section */}
      <section className="relative min-h-screen border-b border-border">
        {/* Hero Carousel */}
        <div className="relative min-h-screen">
          <HeroCarousel />
          {/* Full-area transparent dark overlay */}
          <div className="absolute inset-0 z-10" style={{ background: 'rgba(0,0,0,0.45)' }} />
          
          {/* Hero Text Overlay */}
          <div className="absolute bottom-24 left-6 md:bottom-24 md:translate-y-[63px] md:left-16 z-20 max-w-3xl">
            <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold tracking-tight text-foreground mb-4 reveal text-left">
              Underground Excellence.
              <br />
              <span className="text-gradient-orange">Surface Untouched.</span>
            </h1>
            <p className="text-lg md:text-xl text-white mb-8 max-w-2xl reveal text-left" style={{ transitionDelay: '200ms' }}>
              Horizontal directional drilling for energy, telecommunications, and critical infrastructure across the East Coast
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 reveal items-start" style={{ transitionDelay: '400ms' }}>
              <Link
                href="/contact"
                className="px-8 py-4 bg-primary text-primary-foreground rounded-lg font-bold text-lg hover:bg-primary/90 transition-all flex items-center justify-center gap-2 group"
              >
                RFP Inquiry
                <Icon 
                  name="ArrowRightIcon"
                  size={20}
                  className="group-hover:translate-x-1 transition-transform"
                />
              </Link>
              
              <Link
                href="/projects"
                className="px-8 py-4 border-2 border-foreground text-foreground rounded-lg font-bold text-lg hover:bg-foreground hover:text-background transition-all text-center"
              >
                View Projects
              </Link>
            </div>
          </div>
        </div>
      </section>
      {/* Stats Section */}
      <section className="border-b border-border">
        <div className="grid grid-cols-1 md:grid-cols-3">
          <StatsCard 
            icon="MapIcon"
            value="1250+"
            label="Miles Drilled"
            delay={500}
          />
          <StatsCard 
            icon="ClockIcon"
            value="25+"
            label="Years Experience"
            delay={700}
          />
          <StatsCard 
            icon="ShieldCheckIcon"
            value="0"
            label="Safety Incidents"
            delay={900}
          />
        </div>
      </section>
      {/* Services Section */}
      <section className="py-12 md:py-20 lg:py-32 border-b border-border">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="mb-8 md:mb-16 reveal">
            <span className="text-xs font-mono uppercase tracking-widest text-primary mb-4 block">
              01 / Our Capabilities
            </span>
            <h2 className="text-4xl md:text-6xl font-bold tracking-tight text-foreground mb-6">
              Specialized Drilling Services
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl">
              Advanced horizontal directional drilling solutions for complex underground utility installations.
We handle projects with 2" to 48" diameter, depths to 100ft and lengths up to 1,300ft per bore. We have capabilities to drill in dirt, soft rock, and solid rock utilizing specialized equipment such air hammers, railhead, and mud motors.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            <ServiceCard 
              icon="BoltIcon"
              title="Energy Sector"
              description="Natural gas pipelines, electrical transmission, and oil infrastructure with precision and safety."
              specs={[
                'Pipe diameters: 2" to 48"',
                'Bore lengths: Up to 2,500 feet',
                'Natural gas, electrical, oil pipelines'
              ]}
              delay={100}
            />
            
            <ServiceCard 
              icon="SignalIcon"
              title="Telecommunications"
              description="Fiber optic, cable TV, and 5G infrastructure installations with minimal surface disruption."
              specs={[
                'Cable TV and broadband networks',
                '5G infrastructure deployment',
                'Conduit installation for future expansion'
              ]}
              delay={200}
            />
            
            <ServiceCard 
              icon="BuildingOfficeIcon"
              title="Municipal Utilities"
              description="Water, sewer, and stormwater systems for cities and municipalities across the East Coast."
              specs={[
                'Water and sewer line installation',
                'Stormwater management systems',
                'Utility relocations and upgrades',
              ]}
              delay={300}
            />
            
            <ServiceCard 
              icon="CogIcon"
              title="Industrial Projects"
              description="Solar power, wind turbine field, and data center underground infrastructure."
              specs={[
                'Chemical plant piping systems',
                'Refinery infrastructure',
                'Manufacturing facility utilities',
              ]}
              delay={400}
            />
          </div>
        </div>
      </section>
      {/* Q&A Section */}
      <section className="py-12 md:py-20 lg:py-32 border-b border-border">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="mb-8 md:mb-16 reveal">
            <span className="text-xs font-mono uppercase tracking-widest text-primary mb-4 block">
              02 / Common Questions
            </span>
            <h2 className="text-4xl md:text-6xl font-bold tracking-tight text-foreground mb-6">
              Underground Drilling — Answered
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl">
              The questions we hear most from project owners, engineers, and municipalities before they break ground.
            </p>
          </div>

          <div className="border border-border rounded-2xl overflow-hidden divide-y divide-border">
            {qaItems?.map((item, i) => (
              <div key={i} className="reveal" style={{ transitionDelay: `${i * 80}ms` }}>
                <button
                  className="w-full flex items-center gap-4 px-8 py-6 text-left hover:bg-muted/30 transition-colors"
                  onClick={() => setOpenIndex(openIndex === i ? null : i)}
                  aria-expanded={openIndex === i}
                >
                  <span className="text-xs font-mono text-primary flex-shrink-0 w-6">
                    {String(i + 1)?.padStart(2, '0')}
                  </span>
                  <span className="flex-1 text-base md:text-lg font-bold text-foreground leading-snug">
                    {item?.q}
                  </span>
                  <Icon
                    name="ChevronDownIcon"
                    size={20}
                    className={`flex-shrink-0 text-primary transition-transform duration-300 ${openIndex === i ? 'rotate-180' : ''}`}
                  />
                </button>
                {openIndex === i && (
                  <div className="px-8 pb-6 pl-[4.5rem]">
                    <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                      {item?.a}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* Safety & Certifications Section */}
      <section className="py-12 md:py-20 lg:py-32 border-b border-border">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-16">
            {/* Left: Stats */}
            <SafetyStatsCard />
            
            {/* Right: Certifications */}
            <SafetyCertificationsCard />
          </div>
        </div>
      </section>
      {/* Contact CTA Section */}
      <section id="rfp-inquiry" className="py-12 md:py-20 lg:py-32 bg-card">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-16">
            {/* Left: CTA Content */}
            <div className="reveal">
              <span className="text-xs font-mono uppercase tracking-widest text-primary mb-4 block">
                03 / Get Started
              </span>
              <h2 className="text-4xl md:text-6xl font-bold tracking-tight text-foreground mb-6">
                Ready to Break Ground?
              </h2>
              <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
                Whether you&apos;re planning a major infrastructure project or need emergency underground utility work, 
                our team is ready to deliver. Get a detailed quote within 72 hours.
              </p>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-lg bg-primary/20 flex items-center justify-center flex-shrink-0">
                    <Icon name="DocumentTextIcon" size={24} className="text-primary" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-foreground mb-1">Quote Within 72 Hours</h3>
                    <p className="text-sm text-muted-foreground">
                      Detailed project estimates with equipment specifications, timeline, and transparent pricing.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="mt-8 p-6 bg-muted/30 border border-border rounded-lg">
                <p className="text-sm text-muted-foreground mb-2">
                  <strong className="text-foreground">Need a direct e-mail address?</strong>
                </p>
                <p className="text-2xl font-bold text-primary mb-1">info@eastcoastug.com</p>
                <p className="text-xs text-muted-foreground">Monday-Friday 7am-7pm EST</p>
              </div>
            </div>
            
            {/* Right: Contact Form */}
            <div className="reveal" style={{ transitionDelay: '200ms' }}>
              <div className="bg-background border border-border rounded-2xl p-8">
                <h3 className="text-2xl font-bold text-foreground mb-2">RFP Inquiry</h3>
                <p className="text-sm text-muted-foreground mb-8">
                  Fill out the form below and our team will contact you within 72 hours with a detailed project estimate.
                </p>
                
                <ContactForm />
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Footer */}
      <Footer />
    </main>
  );
}